import React, { useState, useEffect } from 'react';
import { Calendar, FileText, Clock, User, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { appointmentAPI } from '../../../api/appointment';

const PatientHistory = ({ selectedPatient, compact = false }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 10;

  // Fetch patient appointment history
  const fetchPatientHistory = async (page = 1) => {
    const patientId = selectedPatient?.patient?._id || selectedPatient?.patient || selectedPatient?.patientId;
    if (!patientId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await appointmentAPI.getByPatient(patientId, {
        page,
        limit: itemsPerPage,
        sort: '-dateTime', // Most recent first
        status: 'Completed' // Only show completed appointments
      });

      if (response.success) {
        setHistory(response.data.items || []);
        setTotalPages(response.data.pagination?.pages || 1);
        setTotalCount(response.data.pagination?.total || 0);
      } else {
        setError('Failed to fetch patient history');
        setHistory([]);
      }
    } catch (err) {
      console.error('Error fetching patient history:', err);
      setError('Error loading patient history');
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  // Load history when patient changes or page changes
  useEffect(() => {
    if (selectedPatient?.patient?._id) {
      setCurrentPage(1);
      fetchPatientHistory(1);
    } else {
      setHistory([]);
      setTotalCount(0);
      setTotalPages(1);
    }
  }, [selectedPatient]);

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchPatientHistory(page);
  };

  if (!selectedPatient) {
    return (
      <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
        <div className="text-center py-8">
          <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-600 mb-2">No Patient Selected</h3>
          <p className="text-gray-500 text-sm">Select a patient to view their medical history</p>
        </div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'bg-green-100 text-green-800';
      case 'In-Progress': return 'bg-blue-100 text-blue-800';
      case 'Cancelled': return 'bg-red-100 text-red-800';
      case 'Scheduled': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div No completed appointments yet className="bg-white rounded-lg border-2 border-gray-200 overflow-hidden">
      {/* Header */}
      <div className={`bg-[#2D869C] ${compact ? 'px-4 py-2.5' : 'px-5 py-4'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className={`${compact ? 'h-5 w-5' : 'h-6 w-6'} text-white`} />
            <div>
              <h3 className={`text-white font-semibold text-xl ${compact ? 'text-xl' : ''}`}>Patient History</h3>
              {!compact && (
                <p className="font-medium text-gray-200">
                  {selectedPatient?.patient?.firstName} {selectedPatient?.patient?.lastName} <br /> {totalCount} visits
                </p>
              )}
            </div>
          </div>
          <button
            onClick={() => fetchPatientHistory(currentPage)}
            disabled={loading}
            className={`flex items-center gap-1 ${compact ? 'p-1.5' : 'px-3 py-1.5'} bg-white/20 hover:bg-white/30 text-white text-sm rounded-lg transition-colors`}
          >
            <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
            {!compact && <span className="font-medium" >Refresh</span>}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="overflow-hidden">
        {loading ? (
          <div className={`text-center ${compact ? 'py-6' : 'py-12'}`}>
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#2D869C] mx-auto mb-3"></div>
            <div className="text-gray-500 text-xs">Loading history...</div>
          </div>
        ) : error ? (
          <div className={`text-center ${compact ? 'py-6' : 'py-12'}`}>
            <div className="text-red-600 text-sm mb-2">⚠️ {error}</div>
            <button
              onClick={() => fetchPatientHistory(currentPage)}
              className="text-[#2D869C] hover:text-[#236b7a] text-xs underline"
            >
              Try again
            </button>
          </div>
        ) : history.length === 0 ? (
          <div className={`text-center ${compact ? 'py-6' : 'py-12'}`}>
            <Calendar className={`${compact ? 'h-10 w-10' : 'h-16 w-16'} text-gray-300 mx-auto mb-3`} />
            <h4 className={`${compact ? 'text-sm' : 'text-lg'} font-medium text-gray-600 mb-1`}>No History Found</h4>
            <p className="text-black font-medium">No completed appointments yet</p>
          </div>
        ) : (
          <>
            {/* Table - Compact mode shows simplified list */}
            <div className={`overflow-x-auto ${compact ? 'max-h-48' : ''}`}>
              {compact ? (
                // Compact list view
                <div className="divide-y divide-gray-100">
                  {history.slice(0, 5).map((appointment) => (
                    <div 
                      key={appointment._id} 
                      className="px-3 py-2 hover:bg-[#9DC9D4]/10 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-3 w-3 text-[#2D869C]" />
                          <span className="text-xs font-medium text-gray-900">
                            {formatDate(appointment.dateTime)}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500">
                          {appointment.doctor?.name || [appointment.doctor?.firstName, appointment.doctor?.lastName].filter(Boolean).join(' ') || 'Dr.'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 truncate mt-0.5 pl-5">
                        {appointment.complaint || 'No complaint'}
                      </p>
                    </div>
                  ))}
                  {history.length > 5 && (
                    <div className="px-3 py-2 text-center">
                      <span className="text-xs text-[#2D869C]">+{history.length - 5} more visits</span>
                    </div>
                  )}
                </div>
              ) : (
                // Full table view
                <table className="w-full">
                  <thead className="bg-gray-50 border-b-2 border-gray-200">
                    <tr>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Date & Time
                      </th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Chief Complaint
                      </th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Doctor
                      </th>
                     
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {history.map((appointment, index) => (
                      <tr 
                        key={appointment._id} 
                        className="hover:bg-[#9DC9D4]/10 transition-colors"
                      >
                        <td className="px-5 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-[#2D869C]" />
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {formatDate(appointment.dateTime)}
                              </div>
                              <div className="text-xs text-gray-500">
                                {formatTime(appointment.dateTime)}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-3">
                          <div className="text-sm text-gray-700 max-w-md">
                            {appointment.complaint || 'No complaint recorded'}
                          </div>
                        </td>
                        <td className="px-5 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-[#2D869C]" />
                            <div className="text-sm text-gray-900">
                              {appointment.doctor?.name || [appointment.doctor?.firstName, appointment.doctor?.lastName].filter(Boolean).join(' ') || 'Dr. Unknown'}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination - Only show in non-compact mode */}
            {!compact && totalPages > 1 && (
              <div className="px-5 py-3 border-t-2 border-gray-200 bg-gray-50 flex items-center justify-between">
                <div className="text-xs text-gray-500">
                  Showing {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
                    disabled={currentPage === 1}
                    className="flex items-center gap-1 px-2 py-1.5 border-2 border-gray-200 rounded-lg text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="h-3 w-3" />
                    <span>Prev</span>
                  </button>
                  
                  <div className="flex gap-1">
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                      let page;
                      if (totalPages <= 5) {
                        page = i + 1;
                      } else if (currentPage <= 3) {
                        page = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        page = totalPages - 4 + i;
                      } else {
                        page = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`px-2.5 py-1.5 text-xs font-medium rounded-lg ${
                            currentPage === page
                              ? 'bg-[#2D869C] text-white'
                              : 'bg-white text-gray-700 border-2 border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="flex items-center gap-1 px-2 py-1.5 border-2 border-gray-200 rounded-lg text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span>Next</span>
                    <ChevronRight className="h-3 w-3" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PatientHistory;